
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Heart,
  Eye,
  EyeOff,
  LoaderCircle,
} from "lucide-react";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

import { auth, db } from "../firebase";

const inputClass =
  "mt-2 w-full rounded-lg border border-stone-200 px-4 py-3 text-sm text-stone-800 outline-none transition placeholder:text-stone-400 focus:border-rose-800 focus:ring-2 focus:ring-rose-100";

const APPROVED_STATUSES = ["approved", "active"];

function getAuthError(error) {
  const messages = {
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/user-not-found": "No account found with this email.",
    "auth/wrong-password": "Incorrect email or password.",
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/user-disabled":
      "This account has been disabled. Please contact support.",
    "auth/too-many-requests":
      "Too many attempts. Please try again later.",
    "auth/network-request-failed":
      "Network error. Check your internet connection.",
    "auth/unauthorized-domain":
      "This website domain is not authorized in Firebase.",
    "auth/invalid-api-key":
      "Firebase configuration is invalid. Please contact support.",
  };

  return (
    messages[error?.code] ||
    "Unable to sign in. Please try again."
  );
}

function getPendingMessage(role) {
  if (role === "reseller") {
    return "Your reseller application is awaiting payment verification and admin approval.";
  }

  if (role === "shop-owner") {
    return "Your shop owner application is awaiting admin approval.";
  }

  return "Your account is not active yet. Please contact support.";
}

export default function Signin() {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const clearMessages = () => {
    setError("");
    setNotice("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearMessages();
    setLoading(true);

    let signedInUser = null;

    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );

      signedInUser = credential.user;

      // Load the authenticated user's own Firestore profile.
      // Admin access is based on users/{uid}.role, matching the updated rules.
      const profileRef = doc(db, "users", signedInUser.uid);
      const profileSnapshot = await getDoc(profileRef);

      if (!profileSnapshot.exists()) {
        await signOut(auth);
        setError(
          "Your account profile could not be found. Please contact support."
        );
        return;
      }

      const profile = profileSnapshot.data();
      const role = profile.role;
      const status = String(profile.status || "")
        .trim()
        .toLowerCase();
      const applicationStatus = String(
        profile.applicationStatus || ""
      )
        .trim()
        .toLowerCase();

      // Route administrators using the Firestore profile role.
      if (role === "admin") {
        navigate("/admin/dashboard", { replace: true });
        return;
      }

      // Check rejection before checking approval status.
      if (
        status === "rejected" ||
        applicationStatus === "rejected"
      ) {
        await signOut(auth);
        setNotice(
          "Your application was not approved. Please contact support for more information."
        );
        return;
      }

      // Only supported account roles can continue.
      if (role !== "reseller" && role !== "shop-owner") {
        await signOut(auth);
        setError(
          "Your account role is not recognized. Please contact support."
        );
        return;
      }

      // Do not let pending or unverified profiles enter a dashboard.
      if (!APPROVED_STATUSES.includes(status)) {
        await signOut(auth);
        setNotice(getPendingMessage(role));
        return;
      }

      if (role === "reseller") {
        navigate("/reseller/dashboard", { replace: true });
        return;
      }

      if (role === "shop-owner") {
        navigate("/shop-owner/dashboard", { replace: true });
      }
    } catch (err) {
      console.error("Sign-in error:", err);

      // If profile verification failed after authentication,
      // sign out so the user is not left in a partially verified session.
      if (signedInUser && auth.currentUser?.uid === signedInUser.uid) {
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.error("Sign-out after login failure:", signOutError);
        }
      }

      if (
        err?.code === "permission-denied" ||
        err?.code === "unavailable"
      ) {
        setError(
          "Unable to verify your account right now. Check your connection and Firestore rules, then try again."
        );
      } else {
        setError(getAuthError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    clearMessages();

    if (!email.trim()) {
      setError(
        "Enter your email address first to reset your password."
      );
      return;
    }

    setResetLoading(true);

    try {
      await sendPasswordResetEmail(auth, email.trim());

      setNotice(
        "If an account exists for this email, a password reset email will be sent. Check your inbox and spam folder."
      );
    } catch (err) {
      console.error("Password reset error:", err);
      setError(getAuthError(err));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#faf7f5] px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-9"
      >
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-rose-900">
            <Heart size={23} fill="currentColor" />
          </div>

          <h1 className="mt-4 font-serif text-3xl text-rose-950">
            Welcome back
          </h1>

          <p className="mt-2 text-sm leading-6 text-stone-600">
            Sign in to continue to your Vasthra account.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800"
          >
            {error}
          </div>
        )}

        {notice && (
          <div
            role="status"
            className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900"
          >
            {notice}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <label className="block text-sm font-medium text-stone-700">
            Email address

            <input
              name="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                clearMessages();
              }}
              type="email"
              autoComplete="username"
              required
              disabled={loading}
              placeholder="you@example.com"
              className={inputClass}
            />
          </label>

          <label className="block text-sm font-medium text-stone-700">
            Password

            <span className="relative mt-2 block">
              <input
                name="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  clearMessages();
                }}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                disabled={loading}
                placeholder="Enter your password"
                className={`${inputClass} pr-12`}
              />

              <button
                type="button"
                aria-label={
                  showPassword ? "Hide password" : "Show password"
                }
                aria-pressed={showPassword}
                onClick={() =>
                  setShowPassword((current) => !current)
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 transition hover:text-rose-900"
              >
                {showPassword ? (
                  <EyeOff size={18} />
                ) : (
                  <Eye size={18} />
                )}
              </button>
            </span>
          </label>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetLoading || loading}
              className="inline-flex items-center gap-2 text-xs font-semibold text-rose-800 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resetLoading && (
                <LoaderCircle
                  size={14}
                  className="animate-spin"
                />
              )}

              {resetLoading ? "Sending..." : "Forgot password?"}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading || resetLoading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-rose-800 px-5 py-3.5 text-sm font-bold text-white transition hover:bg-rose-950 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              <>
                <LoaderCircle
                  size={17}
                  className="animate-spin"
                />
                Verifying account...
              </>
            ) : (
              <>
                Sign in
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        <p className="mt-7 text-center text-sm text-stone-600">
          Don&apos;t have an account?{" "}
          <Link
            to="/signup"
            className="font-semibold text-rose-800 underline underline-offset-4"
          >
            Sign up
          </Link>
        </p>
      </motion.div>
    </main>
  );
}
